'use client';

import { ButtonHTMLAttributes, MouseEvent } from 'react';

import { Styles, css, cx } from '@flipster/styled-system/css';
import { toggle } from '@flipster/styled-system/recipes';

import { useControllableState } from '../misc/state/use-controllable-state';

// Cannot extend ToggleVariantProps, because ToggleVariantProps expand type to T | Array<T> | ~~~....
export interface ToggleProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onChange' | 'disabled' | 'className'
> {
  required?: boolean;
  disabled?: boolean;
  selected?: boolean;
  onChange?: (selected: boolean) => void;
  css?: Styles;
}

export const Toggle = (props: ToggleProps) => {
  const {
    onChange,
    selected,
    disabled,
    onClick,
    required = false,
    'aria-required': ariaRequired = false,
    css: cssProps,
    ...restProps
  } = props;

  const [innerSelected, setSelected] = useControllableState({
    value: selected,
    defaultValue: false,
    onChange,
  });

  const styles = toggle({ selected: innerSelected, disabled });

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    onClick?.(e);
    setSelected((prev) => !prev);
  };

  return (
    <button
      {...restProps}
      type='button'
      role='switch'
      aria-disabled={disabled}
      disabled={disabled}
      aria-checked={innerSelected}
      aria-required={required || ariaRequired}
      className={cx(styles.root, css(cssProps))}
      onClick={handleClick}
    >
      <div className={styles.backdrop}>
        <span className={styles.dot} />
      </div>
    </button>
  );
};
